import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Search, Calendar, Clock, User, ArrowRight, BookOpen, ArrowLeft } from 'lucide-react';
import { blogData } from '../mock/blogData';
import CodeBlock from '../components/CodeBlock';
import useScrollToTop from '../hooks/useScrollToTop';
import { useTranslation } from '../hooks/useTranslation';
import { useSEO } from '../hooks/useSEO';

const BlogPage = () => {
  useSEO('integrations');
  const { slug } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { language } = useTranslation();

  // Auto scroll to top on route change
  useScrollToTop();

  // If Chinese language is selected, show message that integrations are not available
  if (language === 'zh') {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main className="py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center mb-6">
              <BookOpen className="w-16 h-16 text-slate-600 mb-4" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-6">集成教程暂不支持中文</h1>
            <p className="text-slate-400 mb-8">我们的集成教程目前仅提供英文版本。请切换到英文以查看教程内容。</p>
            <Link to="/">
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
                <ArrowLeft className="w-4 h-4 mr-2" />
                返回首页
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Always call useMemo hook regardless of slug
  const filteredArticles = useMemo(() => {
    if (slug) return []; // Return empty array when viewing individual article
    
    return blogData.articles.filter(article => {
      const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, slug]);

  const featuredArticles = useMemo(() => {
    if (slug) return []; // Return empty array when viewing individual article
    return blogData.articles.filter(article => article.featured);
  }, [slug]);

  // Function to parse content and render code blocks
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentCodeBlock = null;
    let currentLanguage = '';
    let inCodeBlock = false;

    lines.forEach((line, index) => {
      if (line.trim().startsWith('## ')) {
        if (currentCodeBlock) {
          elements.push(
            <CodeBlock key={`code-${elements.length}`} language={currentLanguage}>
              {currentCodeBlock.join('\n')}
            </CodeBlock>
          );
          currentCodeBlock = null;
          inCodeBlock = false;
        }
        elements.push(
          <h2 key={index} className="text-2xl font-bold text-white mt-8 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.trim().startsWith('# ')) {
        if (currentCodeBlock) {
          elements.push(
            <CodeBlock key={`code-${elements.length}`} language={currentLanguage}>
              {currentCodeBlock.join('\n')}
            </CodeBlock>
          );
          currentCodeBlock = null;
          inCodeBlock = false;
        }
        elements.push(
          <h1 key={index} className="text-3xl font-bold text-white mt-8 mb-6">
            {line.replace('# ', '')}
          </h1>
        );
      } else if (line.trim().startsWith('### ')) {
        if (currentCodeBlock) {
          elements.push(
            <CodeBlock key={`code-${elements.length}`} language={currentLanguage}>
              {currentCodeBlock.join('\n')}
            </CodeBlock>
          );
          currentCodeBlock = null;
          inCodeBlock = false;
        }
        elements.push(
          <h3 key={index} className="text-xl font-semibold text-white mt-6 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.trim() && (
        line.includes('pip install') || 
        line.includes('npm install') ||
        line.includes('import ') ||
        line.includes('const ') ||
        line.includes('def ') ||
        line.includes('class ') ||
        line.includes('function ') ||
        line.includes('curl ') ||
        line.includes('http://') ||
        line.includes('export ') ||
        line.includes('scrapy ') ||
        line.includes('chrome.') ||
        line.startsWith('  ') ||
        (inCodeBlock && line.trim())
      )) {
        // This looks like code
        if (!currentCodeBlock) {
          currentCodeBlock = [];
          // Detect language
          if (line.includes('pip install') || line.includes('import ') || line.includes('def ') || line.includes('class ')) {
            currentLanguage = 'python';
          } else if (line.includes('npm install') || line.includes('const ') || line.includes('function ')) {
            currentLanguage = 'javascript';
          } else if (line.includes('curl ') || line.includes('export ')) {
            currentLanguage = 'bash';
          } else {
            currentLanguage = 'text';
          }
          inCodeBlock = true;
        }
        currentCodeBlock.push(line);
      } else {
        // Regular text
        if (currentCodeBlock) {
          elements.push(
            <CodeBlock key={`code-${elements.length}`} language={currentLanguage}>
              {currentCodeBlock.join('\n')}
            </CodeBlock>
          );
          currentCodeBlock = null;
          inCodeBlock = false;
        }
        
        if (line.trim()) {
          if (line.trim().match(/^\d+\./)) {
            // Numbered list item
            elements.push(
              <li key={index} className="text-slate-300 mb-2 ml-4">
                {line.trim()}
              </li>
            );
          } else {
            // Regular paragraph
            elements.push(
              <p key={index} className="text-slate-300 mb-4 leading-relaxed">
                {line}
              </p>
            );
          }
        }
      }
    });

    // Handle any remaining code block
    if (currentCodeBlock) {
      elements.push(
        <CodeBlock key={`code-${elements.length}`} language={currentLanguage}>
          {currentCodeBlock.join('\n')}
        </CodeBlock>
      );
    }

    return elements;
  };

  // Individual Article View
  if (slug) {
    const article = blogData.articles.find(a => a.slug === slug);
    
    if (!article) {
      return (
        <div className="min-h-screen bg-slate-950">
          <Header />
          <main className="py-24">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-4xl font-bold text-white mb-6">Article Not Found</h1>
              <p className="text-slate-400 mb-8">The article you're looking for doesn't exist.</p>
              <Link to="/integrations">
                <Button>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Integrations
                </Button>
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main>
          {/* Back Button - Absolutely positioned to stick right after header */}
          <div className="bg-slate-950 border-b border-slate-800 sticky top-16 z-40 mt-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link 
                to="/integrations"
                className="inline-flex items-center text-slate-400 hover:text-cyan-400 transition-colors duration-200 text-sm py-2"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                Back to Integration Tutorials
              </Link>
            </div>
          </div>

          {/* Article Header */}
          <section className="py-16 bg-gradient-to-b from-slate-950 to-slate-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8">
                <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                  {blogData.categories.find(c => c.slug === article.category)?.name}
                </Badge>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                  {article.title}
                </h1>
                <p className="text-xl text-slate-400 mb-8 max-w-3xl mx-auto">
                  {article.excerpt}
                </p>
                
                <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    <span>{article.author}</span>
                    <span className="ml-1 text-slate-500">• {article.authorRole}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{new Date(article.publishDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </div>
              
              {article.image && (
                <div className="rounded-2xl overflow-hidden border border-slate-800 shadow-2xl">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-64 md:h-96 object-cover"
                  />
                </div>
              )}
            </div>
          </section>

          {/* Article Content */}
          <section className="py-16 bg-slate-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="prose prose-invert prose-slate max-w-none">
                <div className="text-slate-300 leading-relaxed">
                  {renderContent(article.content)}
                </div>
              </div>
              
              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-slate-800">
                <h3 className="text-lg font-semibold text-white mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="border-slate-700 text-slate-300">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Back Button */}
              <div className="mt-8 pt-8 border-t border-slate-800">
                <Link to="/integrations">
                  <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to All Tutorials
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Related Articles - Only show if there are related articles */}
          {(() => {
            const relatedArticles = blogData.articles
              .filter(a => a.id !== article.id && a.category === article.category)
              .slice(0, 3);
            
            if (relatedArticles.length === 0) {
              return null; // Don't render the section at all
            }

            return (
              <section className="py-16 bg-slate-950">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                  <h2 className="text-3xl font-bold text-white mb-12 text-center">Related Articles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {relatedArticles.map((relatedArticle) => (
                      <Link key={relatedArticle.id} to={`/integrations/${relatedArticle.slug}`}>
                        <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all duration-300 h-full group">
                          <CardHeader className="pb-4">
                            <div className="aspect-video rounded-lg overflow-hidden mb-4">
                              <img 
                                src={relatedArticle.image} 
                                alt={relatedArticle.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <Badge className="w-fit mb-2 bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                              {blogData.categories.find(c => c.slug === relatedArticle.category)?.name}
                            </Badge>
                            <h3 className="text-xl font-semibold text-white leading-tight group-hover:text-cyan-400 transition-colors duration-300">
                              {relatedArticle.title}
                            </h3>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <p className="text-slate-400 mb-4">{relatedArticle.excerpt}</p>
                            <div className="flex items-center text-sm text-slate-500">
                              <Clock className="w-4 h-4 mr-1" />
                              <span>{relatedArticle.readTime}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            );
          })()}
        </main>
        <Footer />
      </div>
    );
  }

  // Blog listing page (when no slug)

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center mb-6">
              <BookOpen className="w-8 h-8 text-cyan-400 mr-3" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                Integration <span className="text-cyan-400">Tutorials</span>
              </h1>
            </div>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
              Step-by-step guides for integrating IP9 proxies with popular tools and frameworks. 
              Enhance your development workflow with our comprehensive tutorials.
            </p>
            
            {/* Search and Filter */}
            <div className="max-w-2xl mx-auto">
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search tutorials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder-slate-400 focus:border-cyan-500"
                />
              </div>
              
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory('all')}
                  className={selectedCategory === 'all' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
                >
                  All ({blogData.articles.length})
                </Button>
                {blogData.categories.map((category) => (
                  <Button
                    key={category.slug}
                    variant={selectedCategory === category.slug ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category.slug)}
                    className={selectedCategory === category.slug ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
                  >
                    {category.name} ({category.count})
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        {featuredArticles.length > 0 && searchQuery === '' && selectedCategory === 'all' && (
          <section className="py-16 bg-slate-900">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-white mb-12 text-center">Featured Tutorials</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {featuredArticles.slice(0, 2).map((article) => (
                  <Link key={article.id} to={`/integrations/${article.slug}`}>
                    <Card className="bg-slate-950/50 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 overflow-hidden h-full group">
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={article.image} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <CardContent className="p-8">
                        <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                          {blogData.categories.find(c => c.slug === article.category)?.name}
                        </Badge>
                        <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
                          {article.title}
                        </h3>
                        <p className="text-slate-400 mb-6">{article.excerpt}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-slate-500">
                            <User className="w-4 h-4 mr-2" />
                            <span>{article.author}</span>
                            <span className="mx-2">•</span>
                            <Clock className="w-4 h-4 mr-1" />
                            <span>{article.readTime}</span>
                          </div>
                          
                          <ArrowRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform duration-200" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Articles */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-3xl font-bold text-white">
                {searchQuery ? `Search Results (${filteredArticles.length})` : 'All Tutorials'}
              </h2>
            </div>
            
            {filteredArticles.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No tutorials found</h3>
                <p className="text-slate-400 mb-6">Try adjusting your search terms or category filter.</p>
                <Button 
                  onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map((article) => (
                  <Link key={article.id} to={`/integrations/${article.slug}`}>
                    <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all duration-300 h-full group">
                      <CardHeader className="pb-4">
                        <div className="aspect-video rounded-lg overflow-hidden mb-4">
                          <img 
                            src={article.image} 
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <Badge className="w-fit mb-2 bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                          {blogData.categories.find(c => c.slug === article.category)?.name}
                        </Badge>
                        <h3 className="text-xl font-semibold text-white leading-tight group-hover:text-cyan-400 transition-colors duration-300">
                          {article.title}
                        </h3>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-slate-400 mb-4">{article.excerpt}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-slate-500">
                            <Clock className="w-4 h-4 mr-1" />
                            <span>{article.readTime}</span>
                          </div>
                          
                          <ArrowRight className="w-4 h-4 text-cyan-400 group-hover:translate-x-1 transition-transform duration-200" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;